import { ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { BehaviorSubject, Observable, Subject, of } from 'rxjs';
import { map } from 'rxjs/operators';

import { AccountFacade } from 'ish-core/facades/account.facade';
import { AttributeHelper } from 'ish-core/models/attribute/attribute.helper';
import { Attribute } from 'ish-core/models/attribute/attribute.model';
import { Order } from 'ish-core/models/order/order.model';

type OrderColumnsType =
  | 'creationDate'
  | 'orderNo'
  | 'buyer'
  | 'lineItems'
  | 'status'
  | 'destination'
  | 'lineItems'
  | 'orderTotal';

/**
 * The Order List Component displays the orders provided as input parameter. The number of displayed items can be limited by the maxListItems input parameter.
 * If no order data are provided all orders of the current user will be fetched from store and displayed.
 *
 * @example
 * displays all orders of the current user in a more compact manner.
 * <ish-order-list
 *    maxListItems="0"
 *    [columnsToDisplay]="['creationDate', 'orderNo', 'lineItems', 'status', 'orderTotal']">
 * </ish-order-list>
 */
@Component({
  selector: 'ish-order-list',
  templateUrl: './order-list.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OrderListComponent implements OnInit, OnDestroy {
  /**
   * The maximum number of items to be displayed.
   * Use 0, if you want to display all items without any restrictions.
   * Default: 30 items will be displayed
   */
  @Input() maxListItems: number;
  /**
   * The columns to be displayed.
   * Default: All columns besides the buyer
   */
  @Input() columnsToDisplay: OrderColumnsType[] = [
    'creationDate',
    'orderNo',
    'buyer',
    'lineItems',
    'status',
    'destination',
    'orderTotal',
  ];

  /**
   * The orders to be displayed.
   * Default: Orders of the current user are shown.
   */
  @Input() orders: Partial<Order>[];
  @Input() view = 'OrderHistory';

  form: FormGroup = new FormGroup({});
  orders$: Observable<Partial<Order>[]>;
  loading$: Observable<boolean>;
  private destroy$ = new Subject<void>();

  limit = 5;
  totalPageSize = this.limit;
  offset = 0;
  sortAttribute = '';
  sortDirection = 'desc';
  searchTerm = '';
  sortOptionsMap = {
    'Date Ascending': 'OrderDate-asc',
    'Date Descending': 'OrderDate-desc',
    'Total Ascending': 'OrderTotal-asc',
    'Total Descending': 'OrderTotal-desc',
  };
  selected = 'OrderDate-desc';
  searchPlaceHolder = new BehaviorSubject<string>('Order No');
  searchPlaceHolder$ = this.searchPlaceHolder.asObservable();
  noOrdersMessageKey = 'account.orderlist.no_orders_message';

  constructor(private accountFacade: AccountFacade) {}

  ngOnInit() {
    this.init();
  }
  init() {
    this.resetOrders();
    this.orders$ = (
      this.orders
        ? of(this.orders)
        : this.accountFacade.customOrders$(
            this.limit,
            this.offset,
            this.sortAttribute,
            this.sortDirection,
            this.searchTerm
          )
    ).pipe(map(orders => (this.maxListItems ? orders?.slice(0, this.maxListItems) : orders)));
    this.loading$ = this.accountFacade.ordersLoading$;

    if (!this.orders) {
      this.noOrdersMessageKey = 'account.orderlist.no_placed_orders_message';
    }
  }

  /**
   *  get buyer name from order attributes
   */
  getBuyerName(attributes: Attribute[]): string {
    return `${AttributeHelper.getAttributeValueByAttributeName(
      attributes,
      'firstName'
    )} ${AttributeHelper.getAttributeValueByAttributeName(attributes, 'lastName')}`;
  }

  /**
   *  get buyer name from order
   */
  getBuyerNameUsingOrder(order: Order): string {
    return `${order.user.firstName} ${order.user.lastName}`;
  }

  onScroll(ordersLength: number) {
    if (this.isCustomView() && this.totalPageSize <= ordersLength) {
      this.offset = this.totalPageSize;
      this.totalPageSize = this.offset + this.limit;
      this.accountFacade.customOrders$(
        this.limit,
        this.offset,
        this.sortAttribute,
        this.sortDirection,
        this.searchTerm
      );
    }
  }

  sortBy(sortOption: string) {
    this.selected = sortOption;
    this.resetOrders();
    this.resetPageParameters();
    this.setSortOption(sortOption);
    this.accountFacade.customOrders$(this.limit, this.offset, this.sortAttribute, this.sortDirection, this.searchTerm);
  }

  search(searchTerm: string) {
    this.searchTerm = searchTerm;
    this.resetOrders();
    this.resetPageParameters();
    this.accountFacade.customOrders$(this.limit, this.offset, this.sortAttribute, this.sortDirection, this.searchTerm);
  }
  setSortOption(sortOption: string) {
    const sortOptions = sortOption.split('-');
    this.sortAttribute = sortOptions[0];
    this.sortDirection = sortOptions[1];
  }

  resetPageParameters() {
    this.limit = 5;
    this.offset = 0;
    this.totalPageSize = this.limit;
  }

  resetOrders() {
    this.accountFacade.resetOrders();
  }

  isCustomView() {
    if (this.view === 'OrderHistory') return true;

    return false;
  }

  changePlaceHolder() {
    this.searchPlaceHolder.next('Order No/Buyer Name');
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
